import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import ArticleDetail, { ArticleTags, DynamicShareButtonContainer, TagChip }
from '@fuww/library/src/Article';
import SocialShare from '@fuww/library/src/SocialShare';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import { UserContext } from '../../UserProvider';
import messages from '../../../lib/messages.json';
import FormattedDate from '../../FormattedDate';
import { Link } from '../../../routes.mjs';
import shareList from '../../../lib/shareData';
import { useSiteContext } from '../../SiteContext';
import Image from '../../Image';
import getCategoryTitle from '../../../lib/getCategoryTitle';
import getAuthorSlug from '../../../lib/getAuthorSlug';
import LinkAndAnchor from '../../LinkAndAnchor';
import sizes from './sizes';
import loading from '../../../lib/loading';
import { AccessLevel, OrderStatus } from '../../../lib/graphql/api/graphql';
import ConfirmationAlert from '../../Users/EmailConfirmationAlert';
import SubscriptionCallToAction from '../../SubscriptionCallToAction';
import ArticleAccessForm from '../../LoginSignup/ArticleAccessForm';
import ImageWithUrls from '../../ImageWithUrls';
import getSourceSet from '../../../lib/getSourceSet';
import { authorImageConfigurations } from '../../../lib/imageConfigurations';
import TranslationLinks from './TranslationLinks';

const DynamicNewsletterComponent = dynamic(() => import('../../NewsletterForm'),
  {
    ssr: false,
    loading,
  });

const AuthorTextLink = ({ linkText, authorEmail }) => (
  authorEmail
  && (
    <LinkAndAnchor
      route="author"
      params={{ authorSlug: getAuthorSlug(authorEmail) }}
    >
      {linkText}
    </LinkAndAnchor>
  )
);

AuthorTextLink.propTypes = {
  linkText: PropTypes.string.isRequired,
  authorEmail: PropTypes.string,
};

AuthorTextLink.defaultProps = {
  authorEmail: null,
};

const AuthorImageLink = ({
  authorName,
  authorEmail,
  authorImageUrls,
  authorImageSourceSet,
}) => (
  <LinkAndAnchor
    route="author"
    params={{ authorSlug: getAuthorSlug(authorEmail) }}
  >
    <ImageWithUrls
      imageUrls={authorImageUrls}
      srcSet={authorImageSourceSet}
      sizes="100px"
      alt={`Image of ${authorName}`}
    />
  </LinkAndAnchor>
);

AuthorImageLink.propTypes = {
  authorImageUrls: PropTypes.arrayOf(
    PropTypes.string,
  ),
  authorImageSourceSet: PropTypes.string.isRequired,
  authorEmail: PropTypes.string,
  authorName: PropTypes.string,
};

AuthorImageLink.defaultProps = {
  authorImageUrls: [],
  authorEmail: null,
  authorName: null,
};

const needsPersonalAccount = (user) => (typeof user?.account !== 'object');

const needsLogin = (hasAccess, user) => (!user && !hasAccess);

const needsVerification = (hasAccess, user) => (
  user && !user.verified && !hasAccess
);

const needsSubscription = (hasAccess) => !hasAccess;

const Main = ({
  article,
  ArticleComponent,
  showLoader,
  sourceSet,
  imageUrls,
  metaImageUrl,
  orderStatus,
  fullHeightTitle,
  translationsByLanguage,
  originalArticle,
}) => {
  const {
    body, category, title, dek, insertedAt, tags, labels,
    partnerContent, pressRelease,
    imageCaption, accessLevel, hasAccess, autoTranslated,
  } = article;
  const { origin, url } = useSiteContext();

  const creator = { ...article.creator };

  const { hasLocalNews, locale } = useSiteContext();
  const intl = useIntl();
  const filteredShareList = shareList({
    title,
    media: `${origin}${metaImageUrl}`,
    url,
    locale,
  }).filter((share) => !share.checkMedia || imageUrls[0]);

  const { name: author, email, imageUrls: authorImageUrls = [] } = creator;
  const authorImageSourceSet = getSourceSet(
    authorImageConfigurations,
    authorImageUrls,
  );

  const { user } = useContext(UserContext);

  return (
    <ArticleComponent
      title={title}
      dek={dek}
      category={category || { title: '' }}
      translatedCategoryTitle={getCategoryTitle(category, hasLocalNews, intl)}
      authorName={(
        <AuthorTextLink
          linkText={intl.formatMessage(messages.articleBy, { author })}
          authorEmail={email}
          imageUrls={imageUrls}
        />
      )}
      authorImage={authorImageUrls?.[0] && (
        <AuthorImageLink
          authorName={author}
          authorEmail={email}
          authorImageUrls={authorImageUrls}
          authorImageSourceSet={authorImageSourceSet}
        />
      )}
      insertedAt={<FormattedDate value={insertedAt} />}
      autoTranslated={autoTranslated}
      autoTranslatedText={intl.formatMessage(messages.autoTranslated)}
      translationLinks={(
        <TranslationLinks
          translationsByLanguage={translationsByLanguage}
          originalArticle={originalArticle}
        />
      )}
      body={body}
      image={(
        <Image
          placeholder={imageUrls[0]}
          src={imageUrls[1]}
          srcSet={sourceSet}
          alt={title}
          sizes={sizes}
          finalFetchpriority="high"
          placeholderFetchpriority="high"
          loadStrategy="instant"
          width="100%"
          objectFit="cover"
          preload
        />
      )}
      imageCaption={imageCaption}
      socialButtons={<SocialShare data={filteredShareList} />}
      labels={labels}
      sponsorMessage={
        partnerContent
          ? intl.formatMessage(messages.partnerContent).toUpperCase() : null
      }
      pressReleaseMessage={
        pressRelease
          ? intl.formatMessage(messages.pressReleaseMessage) : null
      }
      showLoader={showLoader}
      isMemberContent={accessLevel !== AccessLevel.FREE}
      needsLogin={needsLogin(hasAccess, user)}
      needsVerification={needsVerification(hasAccess, user)}
      needsSubscription={needsSubscription(hasAccess, user)}
      isProcessingPayment={orderStatus === OrderStatus.NEW}
      LoginComponent={ArticleAccessForm}
      VerificationComponent={ConfirmationAlert}
      SubscriptionComponent={SubscriptionCallToAction}
      needsPersonalAccount={needsPersonalAccount(user)}
      fullHeightTitle={fullHeightTitle}
    >
      <ArticleTags>
        {tags.map(({ title: tagTitle, slug }) => (
          <Link
            route="tag"
            params={{ slug }}
            passHref
            key={`article-${tagTitle}`}
          >
            <TagChip
              label={tagTitle}
            />
          </Link>
        ))}
      </ArticleTags>
      <DynamicShareButtonContainer center>
        <SocialShare data={filteredShareList} />
      </DynamicShareButtonContainer>
      <DynamicNewsletterComponent showDescriptiveText={false} />
    </ArticleComponent>
  );
};

Main.propTypes = {
  article: PropTypes.shape({
    category: PropTypes.shape({
      title: PropTypes.string,
      path: PropTypes.string,
    }),
    title: PropTypes.string,
    dek: PropTypes.string,
    creator: PropTypes.shape({}),
    insertedAt: PropTypes.string,
    imageCaption: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string,
    })),
    body: PropTypes.string,
    reference: PropTypes.string,
    autoTranslated: PropTypes.bool,
    labels: PropTypes.arrayOf(PropTypes.string),
    partnerContent: PropTypes.bool.isRequired,
    pressRelease: PropTypes.bool.isRequired,
    accessLevel: PropTypes.string,
    hasAccess: PropTypes.bool,
  }),
  ArticleComponent: PropTypes.elementType,
  showLoader: PropTypes.bool,
  sourceSet: PropTypes.string.isRequired,
  imageUrls: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  metaImageUrl: PropTypes.string.isRequired,
  orderStatus: PropTypes.string,
  fullHeightTitle: PropTypes.bool,
  translationsByLanguage: PropTypes.shape({}),
  originalArticle: PropTypes.shape({}),
};

Main.defaultProps = {
  article: {},
  ArticleComponent: ArticleDetail,
  showLoader: false,
  orderStatus: null,
  fullHeightTitle: true,
  translationsByLanguage: {},
  originalArticle: null,
};

export default Main;
